import {Icon} from '@wandb/weave/components/Icon';
import React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Label} from 'semantic-ui-react';

import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {TruncateTextTooltip} from '../common/TruncateText/TruncateTextTooltip';
import {TruncationType} from '../common/TruncateText/TruncateTextTypes';
import {EditableLabel} from '../elements/EditableLabel';

export const PanelBankSectionTitle = ({
  sectionName,
  updateName,
  isSectionNameSearchMatch,
  panelSearchMatchCount,
  activePanelCount,
  isSearching,
  isEditingName,
  setIsEditingName,
  onRemovePinnedSection,
}: {
  sectionName: string;
  updateName: (text: string) => void;
  isSectionNameSearchMatch: boolean;
  panelSearchMatchCount: number;
  activePanelCount: number;
  isSearching: boolean;
  isEditingName: boolean;
  setIsEditingName: (editing: boolean) => void;
  onRemovePinnedSection?: (e: React.SyntheticEvent) => void;
}) => {
  const {makeHighlightSearchTitle} = usePanelSearchDebouncedContext();

  if (isEditingName) {
    return (
      <span className="panel-bank__section-title-content">
        <EditableLabel
          title={sectionName}
          onSave={updateName}
          onBlur={() => setIsEditingName(false)}
          editing={isEditingName}
          serverText={sectionName}
          onClick={(e: React.SyntheticEvent) => {
            // we use the menu option to trigger editing mode, rather than direct click
            e.preventDefault();
          }}
          dataTest={'panel-bank-section-title-label'}
        />
        <PanelBankSectionCountLabel
          isSectionNameSearchMatch={isSectionNameSearchMatch}
          panelSearchMatchCount={panelSearchMatchCount}
          activePanelCount={activePanelCount}
          isSearching={isSearching}
        />
        <PanelBankSectionPinnedTag onRemove={onRemovePinnedSection} />
      </span>
    );
  }

  return (
    <span className="panel-bank__section-title-content">
      <TruncateTextTooltip
        truncationType={TruncationType.End}
        className="text-left"
        noLink
        fullText={sectionName}
        highlight={makeHighlightSearchTitle}
        dataTest={'panel-bank-section-title-label'}
      />
      <PanelBankSectionCountLabel
        isSectionNameSearchMatch={isSectionNameSearchMatch}
        panelSearchMatchCount={panelSearchMatchCount}
        activePanelCount={activePanelCount}
        isSearching={isSearching}
      />
      <PanelBankSectionPinnedTag onRemove={onRemovePinnedSection} />
    </span>
  );
};

const PanelBankSectionCountLabel = ({
  isSectionNameSearchMatch,
  panelSearchMatchCount,
  activePanelCount,
  isSearching,
}: {
  isSectionNameSearchMatch: boolean;
  panelSearchMatchCount: number;
  activePanelCount: number;
  isSearching: boolean;
}) => {
  if (isSearching) {
    const labelText = isSectionNameSearchMatch
      ? activePanelCount
      : `${panelSearchMatchCount} of ${activePanelCount}`;
    return (
      <Label
        className="count-label"
        aria-label={`Visible panels: ${labelText}`}>
        {labelText}
      </Label>
    );
  }

  return <Label className="count-label">{activePanelCount}</Label>;
};

const PanelBankSectionPinnedTag = ({
  onRemove,
}: {
  onRemove?: (e: React.SyntheticEvent) => void;
}) => {
  if (onRemove == null) {
    return null;
  }

  return (
    <div
      onClick={onRemove}
      className="flex h-22 w-[86px] items-center rounded-sm bg-moon-300 px-3 py-6 text-sm text-moon-600 hover:bg-teal-300 hover:text-teal-600">
      <Icon className="mr-4 h-14 w-14" name="pin" />
      <span className="mr-4">Pinned</span>
      <Icon className="mr-4 h-14 w-14" name="close" />
    </div>
  );
};
